import { useState, useEffect, useRef } from 'react';
import { Heart, Star, MapPin, Grid, List, Sparkles, LogIn, Trash2, ChefHat, TrendingUp, Search, Clock, Award, Edit2, Check, X as XIcon, Share2, FolderPlus, Folder, Plus, Camera, Users, Bookmark, Layers, Trophy, Fingerprint, RefreshCw } from 'lucide-react';
import FoodCardIcon from '../components/FoodCardIcon';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfileEmptyCards from '../components/ProfileEmptyCards';
import { useFoodlist } from '../contexts/FoodlistContext';
import { useLocation } from '../contexts/LocationContext';
import { useToast } from '../hooks/use-toast';
import AuthModal from '../components/AuthModal';
import LocationSearchInput from '../components/LocationSearchInput';
import TasteProfileShareCard from '../components/TasteProfileShareCard';
import DiscoverFriends from '../components/DiscoverFriends';
import InstagramConnect from '../components/InstagramConnect';
import StreakDisplay from '../components/StreakDisplay';
import FollowListModal from '../components/FollowListModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CUISINE_COLORS = {
  Italian: 'bg-green-100 text-green-700 border-green-200',
  Japanese: 'bg-red-100 text-red-700 border-red-200',
  Indian: 'bg-orange-100 text-orange-700 border-orange-200',
  Thai: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Chinese: 'bg-red-100 text-red-700 border-red-200',
  Mexican: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  French: 'bg-blue-100 text-blue-700 border-blue-200',
  Mediterranean: 'bg-teal-100 text-teal-700 border-teal-200',
  Korean: 'bg-purple-100 text-purple-700 border-purple-200',
  American: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  default: 'bg-gray-100 text-gray-700 border-gray-200',
};

const getCuisineColor = (c) => CUISINE_COLORS[c] || CUISINE_COLORS.default;

const COMFORT_LABELS = {
  'dine-out': '🍽️ Dine Out',
  'dine-in': '🏠 Delivery',
  'cook-at-home': '👨‍🍳 Cooking',
  'meal-plan': '📅 Meal Plan',
};

/* ── Taste DNA Inline (for Profile tab) ───────────────── */
const SCORE_META = {
  adventurousness: { label: 'Adventurous', color: '#FF6B35' },
  health_conscious: { label: 'Health-Aware', color: '#10B981' },
  social_foodie: { label: 'Social Foodie', color: '#3B82F6' },
  home_chef: { label: 'Home Chef', color: '#8B5CF6' },
  night_owl: { label: 'Night Owl', color: '#6366F1' },
  budget_savvy: { label: 'Budget Savvy', color: '#F59E0B' },
};

const FLAVOR_COLORS = {
  sweet: '#F472B6', savory: '#F59E0B', spicy: '#EF4444',
  umami: '#8B5CF6', fresh: '#34D399', rich: '#D97706',
};

const MiniRadar = ({ flavors = {} }) => {
  const keys = ['sweet', 'savory', 'spicy', 'umami', 'fresh', 'rich'];
  const cx = 100, cy = 100, r = 75;
  const points = keys.map((k, i) => {
    const angle = (Math.PI * 2 * i) / keys.length - Math.PI / 2;
    const val = (flavors[k] || 0) / 100;
    return { x: cx + r * val * Math.cos(angle), y: cy + r * val * Math.sin(angle), lx: cx + (r + 15) * Math.cos(angle), ly: cy + (r + 15) * Math.sin(angle), key: k };
  });
  const gridLines = [0.33, 0.66, 1].map(s => keys.map((_, i) => { const a = (Math.PI * 2 * i) / keys.length - Math.PI / 2; return `${cx + r * s * Math.cos(a)},${cy + r * s * Math.sin(a)}`; }).join(' '));
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[200px] mx-auto">
      {gridLines.map((pts, i) => <polygon key={i} points={pts} fill="none" stroke="#E5E7EB" strokeWidth="0.5" />)}
      <polygon points={points.map(p => `${p.x},${p.y}`).join(' ')} fill="rgba(255,107,53,0.15)" stroke="#FF6B35" strokeWidth="2" />
      {points.map((p, i) => (
        <g key={i}><circle cx={p.x} cy={p.y} r="3" fill={FLAVOR_COLORS[p.key]} />
          <text x={p.lx} y={p.ly} textAnchor="middle" dominantBaseline="middle" className="text-[8px] font-semibold fill-gray-500 capitalize">{p.key}</text></g>
      ))}
    </svg>
  );
};

const TasteDNAInline = ({ user }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);
  const token = localStorage.getItem('fq_auth_token');
  const hdrs = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/taste-dna/profile`, { headers: hdrs, credentials: 'include' });
        if (res.ok) { const d = await res.json(); setProfile(d.profile); }
      } catch {} finally { setLoading(false); }
    })();
  }, [token]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/taste-dna/regenerate`, { method: 'POST', headers: hdrs, credentials: 'include' });
      if (res.ok) { const d = await res.json(); setProfile(d.profile); }
    } catch {} finally { setRegenerating(false); }
  };

  const exportCard = async () => {
    setExporting(true);
    try {
      const el = cardRef.current;
      if (!el) return;
      // Use html2canvas-like approach via canvas API
      const canvas = document.createElement('canvas');
      const scale = 2;
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');

      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, 0, 1920);
      grad.addColorStop(0, '#1a1a2e');
      grad.addColorStop(0.4, '#16213e');
      grad.addColorStop(1, '#0f0f23');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1080, 1920);

      // FoodQuest logo area
      ctx.fillStyle = '#FF6B35';
      ctx.beginPath();
      ctx.roundRect(440, 80, 200, 60, 20);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 28px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('FoodQuest', 540, 120);

      // Personality title
      ctx.fillStyle = '#F4C430';
      ctx.font = 'bold 14px -apple-system, sans-serif';
      ctx.fillText('YOUR TASTE DNA', 540, 200);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 56px -apple-system, sans-serif';
      const title = profile?.personality?.title || 'The Foodie';
      ctx.fillText(title, 540, 270);

      // Subtitle
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '24px -apple-system, sans-serif';
      ctx.fillText(profile?.personality?.subtitle || '', 540, 320);

      // Motto
      if (profile?.food_motto) {
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = 'italic 22px -apple-system, sans-serif';
        ctx.fillText(`"${profile.food_motto}"`, 540, 380);
      }

      // Score bars
      const scores = profile?.scores || {};
      let y = 460;
      ctx.textAlign = 'left';
      Object.entries(scores).forEach(([key, val]) => {
        const meta = SCORE_META[key];
        if (!meta) return;
        // Label
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = '20px -apple-system, sans-serif';
        ctx.fillText(meta.label, 100, y);
        // Value
        ctx.fillStyle = meta.color;
        ctx.font = 'bold 20px -apple-system, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`${val}%`, 980, y);
        ctx.textAlign = 'left';
        // Bar bg
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.beginPath();
        ctx.roundRect(100, y + 10, 880, 12, 6);
        ctx.fill();
        // Bar fill
        ctx.fillStyle = meta.color;
        ctx.beginPath();
        ctx.roundRect(100, y + 10, 880 * val / 100, 12, 6);
        ctx.fill();
        y += 55;
      });

      // Cuisine breakdown
      y += 20;
      ctx.fillStyle = '#F4C430';
      ctx.font = 'bold 14px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('CUISINE BREAKDOWN', 540, y);
      y += 40;
      const cuisines = profile?.cuisine_breakdown || [];
      const barWidth = 800 / Math.max(cuisines.length, 1);
      cuisines.forEach((c, i) => {
        const bx = 140 + i * barWidth;
        const bh = c.percentage * 3;
        // Bar
        ctx.fillStyle = '#FF6B35';
        ctx.beginPath();
        ctx.roundRect(bx + 10, y + 300 - bh, barWidth - 20, bh, 8);
        ctx.fill();
        // Label
        ctx.fillStyle = '#ffffff';
        ctx.font = '16px -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(c.emoji || '', bx + barWidth / 2, y + 330);
        ctx.font = '14px -apple-system, sans-serif';
        ctx.fillText(c.cuisine, bx + barWidth / 2, y + 355);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText(`${c.percentage}%`, bx + barWidth / 2, y + 375);
      });

      y += 410;
      // Spirit dish
      if (profile?.spirit_dish?.name) {
        ctx.fillStyle = '#F4C430';
        ctx.font = 'bold 14px -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('SPIRIT DISH', 540, y);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 36px -apple-system, sans-serif';
        ctx.fillText(profile.spirit_dish.name, 540, y + 50);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = 'italic 18px -apple-system, sans-serif';
        ctx.fillText(profile.spirit_dish.why || '', 540, y + 85);
        y += 120;
      }

      // Footer
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '18px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Discover yours at foodquest.app', 540, 1870);

      // Export
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], 'taste-dna.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title: 'My Taste DNA', text: `I'm a "${title}" on FoodQuest!` }); } catch {}
        } else {
          // Fallback: download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = 'taste-dna.png'; a.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (e) {
      console.error('Export failed:', e);
    } finally { setExporting(false); }
  };

  if (loading) return (
    <div className="space-y-4 py-8">
      {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />)}
    </div>
  );

  if (!profile) return (
    <div className="text-center py-16">
      <Fingerprint className="w-12 h-12 text-gray-200 mx-auto mb-3" />
      <p className="text-sm text-gray-500">Couldn't load your Taste DNA. Try again later.</p>
    </div>
  );

  const pers = profile.personality || {};
  const scores = profile.scores || {};
  const cuisines = profile.cuisine_breakdown || [];
  const flavors = profile.flavor_profile || {};
  const traits = profile.top_traits || [];
  const spirit = profile.spirit_dish || {};
  const recap = profile.weekly_recap || {};
  const recs = profile.recommendations || [];

  return (
    <div className="space-y-6" ref={cardRef} data-testid="taste-dna-inline">
      {/* Hero card */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-[#1a1a2e] rounded-2xl p-6 text-white text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B35] to-[#F4C430] rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-[#FF6B35]/20">
          <Fingerprint className="w-8 h-8 text-white" />
        </div>
        <p className="text-[#F4C430] text-[10px] font-bold uppercase tracking-widest mb-1">Your Taste DNA</p>
        <h2 className="text-2xl font-black mb-1" data-testid="inline-dna-title">{pers.title || 'The Foodie'}</h2>
        <p className="text-white/50 text-sm">{pers.subtitle || ''}</p>
        {profile.food_motto && (
          <p className="mt-3 text-sm italic text-white/40">"{profile.food_motto}"</p>
        )}
        <div className="flex items-center justify-center gap-2 mt-4">
          <button onClick={handleRegenerate} disabled={regenerating}
            className="flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
            data-testid="inline-regenerate-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Analyzing...' : 'Refresh'}
          </button>
          <button onClick={exportCard} disabled={exporting}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#FF6B35] hover:bg-[#E55A2B] rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
            data-testid="export-card-btn">
            <Share2 className={`w-3.5 h-3.5 ${exporting ? 'animate-spin' : ''}`} />
            {exporting ? 'Creating...' : 'Share Card'}
          </button>
          <button onClick={() => navigate('/taste-dna')}
            className="flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-semibold transition-all"
            data-testid="full-dna-btn">
            Full Dashboard
          </button>
        </div>
      </div>

      {/* Scores grid */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#FF6B35]" /> Foodie Scores
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(scores).map(([key, val]) => {
            const meta = SCORE_META[key];
            if (!meta) return null;
            return (
              <div key={key} className="flex items-center gap-2" data-testid={`inline-score-${key}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-semibold text-gray-600">{meta.label}</span>
                    <span className="text-[10px] font-bold" style={{ color: meta.color }}>{val}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: meta.color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cuisine + Radar side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h3 className="font-bold text-gray-900 text-sm mb-3">Cuisine Breakdown</h3>
          <div className="space-y-2">
            {cuisines.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-base">{c.emoji}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-800">{c.cuisine}</span>
                    <span className="text-xs font-bold text-[#FF6B35]">{c.percentage}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full mt-0.5 overflow-hidden">
                    <div className="h-full bg-[#FF6B35] rounded-full" style={{ width: `${c.percentage}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h3 className="font-bold text-gray-900 text-sm mb-2">Flavor Profile</h3>
          <MiniRadar flavors={flavors} />
        </div>
      </div>

      {/* Spirit dish + Traits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#FFF5F0] to-white rounded-2xl border border-[#FF6B35]/10 p-5">
          <h3 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-[#F4C430]" /> Spirit Dish
          </h3>
          <p className="text-lg font-black text-gray-900">{spirit.name || '?'}</p>
          <p className="text-xs text-gray-500 italic mt-1">{spirit.why || ''}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h3 className="font-bold text-gray-900 text-sm mb-2">Top Traits</h3>
          {traits.map((t, i) => (
            <div key={i} className="flex items-start gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3 text-[#FF6B35] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600">{t}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Memory Lane mini */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#F4C430]" />
          <h3 className="font-bold text-sm">Food Memory Lane</h3>
          <span className="text-[9px] bg-white/10 px-1.5 py-0.5 rounded-full">This Week</span>
        </div>
        <p className="text-base font-bold text-[#F4C430] mb-3">{recap.headline || 'Your food journey continues...'}</p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-white/5 rounded-xl p-2.5 text-center">
            <p className="text-xl font-black text-[#FF6B35]">{recap.new_cuisines_tried || 0}</p>
            <p className="text-[9px] text-white/50">New Cuisines</p>
          </div>
          <div className="bg-white/5 rounded-xl p-2.5 text-center">
            <p className="text-xl font-black text-[#3B82F6]">{recap.dishes_discovered || 0}</p>
            <p className="text-[9px] text-white/50">Dishes</p>
          </div>
          <div className="bg-white/5 rounded-xl p-2.5 text-center">
            <p className="text-xl font-black text-[#10B981]">{recap.streak_days || 0}</p>
            <p className="text-[9px] text-white/50">Streak</p>
          </div>
        </div>
        {recap.challenge && (
          <div className="bg-[#FF6B35]/20 border border-[#FF6B35]/30 rounded-xl p-3">
            <p className="text-[10px] text-[#FF6B35] uppercase tracking-wider font-bold mb-0.5">Next Week Challenge</p>
            <p className="text-xs text-white/80">{recap.challenge}</p>
          </div>
        )}
      </div>

      {/* AI recommendations */}
      {recs.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#FF6B35]" /> Try Next
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {recs.map((r, i) => (
              <button key={i} onClick={() => navigate(`/ai-chat?q=${encodeURIComponent(r.dish)}`)}
                className="text-left p-3 bg-gray-50 hover:bg-[#FFF5F0] rounded-xl transition-all border border-gray-100 hover:border-[#FF6B35]/20"
                data-testid={`inline-rec-${i}`}>
                <p className="font-bold text-gray-900 text-xs">{r.dish}</p>
                <p className="text-[9px] text-[#FF6B35] font-medium">{r.cuisine}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{r.why}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const { user, loading, logout, refetchUser } = useAuth();
  const { items: foodlist, removeById: removeFoodlistItem, refetch: fetchFoodlist } = useFoodlist();
  const { setGeocodedLocation } = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('posts');
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Saved Addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressLabelType, setAddressLabelType] = useState('home');
  const [customAddressLabel, setCustomAddressLabel] = useState('');
  const [addressLocation, setAddressLocation] = useState(null);
  const [addressNotes, setAddressNotes] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);

  // Collections
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null); // null = show all
  const [newCollName, setNewCollName] = useState('');
  const [showNewColl, setShowNewColl] = useState(false);

  const fetchCollections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/foodlist/collections`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setCollections(d.collections || []); }
    } catch {}
  };

  const createCollection = async () => {
    if (!newCollName.trim()) return;
    try {
      await fetch(`${API_URL}/api/foodlist/collections`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCollName.trim() }),
      });
      setNewCollName(''); setShowNewColl(false);
      fetchCollections();
    } catch {}
  };

  const deleteCollection = async (collId) => {
    await fetch(`${API_URL}/api/foodlist/collections/${collId}`, { method: 'DELETE', credentials: 'include' });
    if (activeCollection === collId) setActiveCollection(null);
    fetchCollections();
  };

  // Collection Sets (Pokemon Card Sets)
  const [collectionSets, setCollectionSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [expandedSet, setExpandedSet] = useState(null);
  const [collectingCard, setCollectingCard] = useState(null);

  const fetchCollectionSets = async () => {
    setSetsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/collection-sets/featured`, { credentials: 'include' });
      if (res.ok) setCollectionSets((await res.json()).sets || []);
    } catch {} finally { setSetsLoading(false); }
  };

  const handleCollectCard = async (setId, cardName) => {
    setCollectingCard(`${setId}-${cardName}`);
    try {
      const res = await fetch(`${API_URL}/api/collection-sets/${setId}/collect`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_name: cardName }),
      });
      if (res.ok) {
        const data = await res.json();
        setCollectionSets(prev => prev.map(s =>
          s.set_id === setId ? { ...s, collected: data.collected_cards, collected_count: data.collected_count, completed: data.just_completed || s.completed } : s
        ));
      }
    } catch {} finally { setCollectingCard(null); }
  };

  const assignToCollection = async (itemId, collId) => {
    await fetch(`${API_URL}/api/foodlist/items/${itemId}/collection`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ collection_id: collId }),
    });
    fetchFoodlist();
  };

  const fetchAddresses = async () => {
    setAddressesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/addresses`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setSavedAddresses(d.addresses || []);
      }
    } catch { /* silent */ } finally {
      setAddressesLoading(false);
    }
  };

  const resetAddressForm = () => {
    setAddressLabelType('home');
    setCustomAddressLabel('');
    setAddressLocation(null);
    setAddressNotes('');
  };

  const createAddress = async () => {
    if (!addressLocation?.displayName) return;
    setAddressSaving(true);
    const labelRaw = addressLabelType === 'custom' ? (customAddressLabel || 'Custom') : addressLabelType;
    const label = labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);
    try {
      const res = await fetch(`${API_URL}/api/addresses`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label,
          display_name: addressLocation.displayName,
          full_address: addressLocation.fullAddress,
          lat: addressLocation.lat,
          lng: addressLocation.lng,
          notes: addressNotes,
        }),
      });
      if (res.ok) {
        resetAddressForm();
        setShowAddressForm(false);
        fetchAddresses();
      }
    } catch { /* silent */ } finally {
      setAddressSaving(false);
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      await fetch(`${API_URL}/api/addresses/${addressId}`, { method: 'DELETE', credentials: 'include' });
      fetchAddresses();
    } catch { /* silent */ }
  };

  const handleUseAddress = (address) => {
    if (!address?.display_name) return;
    setGeocodedLocation({
      city: address.display_name.split(',')[0],
      displayName: address.display_name,
      lat: address.lat,
      lng: address.lng,
      fullAddress: address.full_address || address.display_name,
    });
  };

  useEffect(() => { if (user) { fetchCollections(); fetchAddresses(); fetchCollectionSets(); } }, [user]); // eslint-disable-line

  // Taste profile
  const [tasteProfile, setTasteProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);

  // Activity summary
  const [activitySummary, setActivitySummary] = useState(null);

  // Real user posts
  const [myPosts, setMyPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [myReviews, setMyReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [myReposts, setMyReposts] = useState({ total_reposts: 0 });

  // Social stats
  const [socialStats, setSocialStats] = useState({ followers_count: 0, following_count: 0 });
  const [followModal, setFollowModal] = useState({ open: false, tab: 'followers' });

  // Points/level
  const [points, setPoints] = useState({ total: 0, level: 'Food Curious', level_emoji: '🌱' });

  // Profile bio/name edit
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchFoodlist();
      fetchActivitySummary();
      fetchMyPosts();
      fetchSocialStats();
      fetchMyReviews();
      fetchMyReposts();
    }
  }, [user, fetchFoodlist]);

  const fetchActivitySummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/summary`, { credentials: 'include' });
      if (res.ok) setActivitySummary(await res.json());
    } catch { /* silent */ }
  };

  const fetchMyPosts = async () => {
    setPostsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/mine`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setMyPosts(d.posts || []); }
    } catch { /* silent */ } finally { setPostsLoading(false); }
  };

  const fetchSocialStats = async () => {
    if (!user?.user_id) return;
    try {
      const res = await fetch(`${API_URL}/api/social/stats/${user.user_id}`, { credentials: 'include' });
      if (res.ok) setSocialStats(await res.json());
    } catch { /* silent */ }
  };

  const fetchMyReviews = async () => {
    setReviewsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reviews/mine`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setMyReviews(d.reviews || []); }
    } catch { /* silent */ } finally { setReviewsLoading(false); }
  };

  const fetchMyReposts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/claims/my`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setMyReposts(d); }
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/points/balance`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : {})
      .then(d => { if (d.total !== undefined) setPoints(d); })
      .catch(() => {});
  }, [user]);

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setMyPosts(prev => prev.filter(p => p.post_id !== postId));
        toast({ title: 'Post deleted successfully' });
      } else {
        throw new Error('Failed to delete');
      }
    } catch { 
      toast({ title: 'Failed to delete post', variant: 'destructive' });
    }
  };

  const fetchTasteProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/activity/taste-profile`, { credentials: 'include' });
      if (res.ok) setTasteProfile(await res.json());
    } catch { /* silent */ } finally {
      setProfileLoading(false);
    }
  };

  // Load taste profile when user visits that tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'taste' && !tasteProfile && !profileLoading) {
      fetchTasteProfile();
    }
  };

  const startEdit = () => {
    setEditName(user.name || '');
    setEditBio(user.bio || '');
    setEditUsername((user.username || '').replace('@', ''));
    setEditMode(true);
  };

  const cancelEdit = () => setEditMode(false);

  const saveProfile = async () => {
    setEditSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName, bio: editBio, username: editUsername }),
      });
      if (res.ok) {
        await refetchUser();
        setEditMode(false);
      }
    } catch { /* silent */ } finally {
      setEditSaving(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-6 px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view your profile</h2>
          <p className="text-gray-600 mb-6">Track your food discoveries, saved dishes, and personalised recommendations</p>
          <Button data-testid="profile-signin-btn" onClick={() => setShowAuthModal(true)} className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white px-8 py-3 text-lg">
            Sign In
          </Button>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  const displayName = user.name || 'Foodie';
  const initials = displayName.split(' ').map((n) => n[0]).join('').toUpperCase();
  const topCuisines = activitySummary?.top_cuisines || [];
  const addressMapUrl = addressLocation?.lat && addressLocation?.lng
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${addressLocation.lng - 0.04},${addressLocation.lat - 0.04},${addressLocation.lng + 0.04},${addressLocation.lat + 0.04}&layer=mapnik&marker=${addressLocation.lat},${addressLocation.lng}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Profile Header — Home page hero style */}
      <div className="relative" style={{ height: 'clamp(340px, 45vh, 440px)' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=80')" }} />

        <div className="relative h-full flex flex-col justify-end max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-5">
          {/* Profile text — with dark backdrop for readability */}
          <div className="mb-4 w-full">
            <div className="flex items-start gap-4 bg-black/45 backdrop-blur-md rounded-2xl p-4 md:p-5">
              <Avatar className="w-20 h-20 md:w-24 md:h-24 ring-3 ring-[#FF6B35] ring-offset-2 ring-offset-black/20 shadow-xl flex-shrink-0">
                <AvatarImage src={user.picture} alt={displayName} />
                <AvatarFallback className="text-2xl md:text-3xl bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white font-bold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                {editMode ? (
                  <input data-testid="edit-name-input" value={editName} onChange={(e) => setEditName(e.target.value)}
                    className="text-2xl font-bold bg-white/20 text-white border-b-2 border-white/50 outline-none rounded px-2 py-0.5 max-w-xs" />
                ) : (
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.1] tracking-tight">{displayName}</h1>
                )}
                {editMode ? (
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-white/60 text-sm">@</span>
                    <input data-testid="edit-username-input" value={editUsername} onChange={(e) => setEditUsername(e.target.value)}
                      placeholder="username" className="text-sm bg-white/20 text-white border-b border-white/40 outline-none rounded px-2 py-0.5 w-32" />
                  </div>
                ) : (
                  <p className="text-white/70 text-xs md:text-sm mt-1">
                    {user.username}
                    <button onClick={() => setFollowModal({ open: true, tab: 'followers' })} className="text-white/50 ml-2 hover:text-[#F4C430] transition-colors" data-testid="open-followers-modal">
                      <span className="font-semibold text-white/70">{socialStats.followers_count}</span> followers
                    </button>
                    <span className="text-white/40 mx-1">·</span>
                    <button onClick={() => setFollowModal({ open: true, tab: 'following' })} className="text-white/50 hover:text-[#F4C430] transition-colors" data-testid="open-following-modal">
                      <span className="font-semibold text-white/70">{socialStats.following_count}</span> following
                    </button>
                  </p>
                )}
                {editMode ? (
                  <textarea data-testid="edit-bio-input" value={editBio} onChange={(e) => setEditBio(e.target.value)}
                    placeholder="Tell us about your food journey..." rows={2}
                    className="w-full max-w-md text-sm bg-white/20 text-white border border-white/40 outline-none rounded-lg px-3 py-2 mt-2 resize-none placeholder:text-white/50" />
                ) : (
                  <p className="text-white/80 text-xs md:text-sm mt-1.5 font-light">{user.bio || 'Food enthusiast | Global foodie'}</p>
                )}
              </div>
              {/* Edit / Save */}
              <div className="flex-shrink-0">
                {editMode ? (
                  <div className="flex gap-2">
                    <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={editSaving} size="sm" className="bg-white text-[#FF6B35] hover:bg-white/90 font-semibold">
                      {editSaving ? '...' : <><Check className="w-4 h-4 mr-1" />Save</>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-white hover:bg-white/20">
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button data-testid="edit-profile-btn" onClick={startEdit} size="sm" className="bg-white/20 hover:bg-white/30 text-white border border-white/30">
                    <Edit2 className="w-4 h-4 mr-1.5" />Edit
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Dark glass icon bar with stats + FoodCard */}
          <div className="w-full bg-black/45 backdrop-blur-md rounded-2xl p-3 md:p-4" data-testid="profile-stats-bar">
            <div className="flex items-center justify-around">
              {[
                { key: 'posts', icon: null, count: myPosts.length, label: 'FoodCards', customIcon: true },
                { key: 'reviews', icon: Star, count: myReviews.length, label: 'Reviews' },
                { key: 'foodist', icon: Layers, count: foodlist.length, label: 'Foodlist' },
                { key: 'reposts', icon: null, count: myReposts.total_reposts || 0, label: 'Reposts', repostIcon: true },
                { key: 'sets', icon: ChefHat, count: collectionSets.length, label: 'Saved Recipes' },
                { key: 'taste-dna', icon: Fingerprint, count: null, label: 'Taste DNA' },
              ].map(({ key, icon: StatIcon, count, label, customIcon, repostIcon }) => (
                <button
                  key={key}
                  onClick={() => handleTabChange(key)}
                  data-testid={`tab-${key}`}
                  className={`flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all ${activeTab === key ? 'text-[#F4C430]' : 'text-white/60 hover:text-white/90'}`}
                >
                  {customIcon ? (
                    <FoodCardIcon size={22} className="text-[#FF6B35]" />
                  ) : repostIcon ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
                  ) : (
                    <StatIcon className="w-5 h-5" />
                  )}
                  <span className="text-[10px] md:text-xs font-medium">
                    {count !== null ? `${count} ` : ''}{label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Hidden TabsList for Radix to work */}
          <TabsList className="hidden">
            <TabsTrigger value="posts" />
            <TabsTrigger value="reviews" />
            <TabsTrigger value="foodist" />
            <TabsTrigger value="reposts" />
            <TabsTrigger value="sets" />
            <TabsTrigger value="taste-dna" />
          </TabsList>

          {/* Posts Tab — real user posts */}
          <TabsContent value="posts">
            {postsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-square bg-gray-200 rounded-2xl animate-pulse" />)}
              </div>
            ) : myPosts.length === 0 ? (
              <ProfileEmptyCards onTabChange={handleTabChange} />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {myPosts.map((post) => {
                  const PRIVACY_COLORS = { public: 'bg-green-500', friends: 'bg-blue-500', family: 'bg-purple-500', private: 'bg-gray-500' };
                  return (
                    <Card key={post.post_id} className="overflow-hidden group cursor-pointer hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg rounded-2xl relative">
                      <div className="relative aspect-square">
                        <img
                          src={post.image_url}
                          alt={post.caption}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Privacy badge */}
                        <div className={`absolute top-2 left-2 ${PRIVACY_COLORS[post.privacy] || 'bg-gray-500'} text-white text-[10px] px-2 py-0.5 rounded-full font-semibold`}>
                          {post.privacy === 'private' ? 'Only Me' : post.privacy?.charAt(0).toUpperCase() + post.privacy?.slice(1)}
                        </div>
                        {/* Delete on hover */}
                        <button
                          onClick={() => deletePost(post.post_id)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white rounded-full p-2 hover:bg-red-600 min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Delete post"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white flex items-center gap-4">
                            <span className="flex items-center gap-1 text-sm"><Heart className="w-4 h-4 fill-white" />{post.likes_count || 0}</span>
                          </div>
                        </div>
                      </div>
                      {post.caption && (
                        <div className="p-2">
                          <p className="text-xs text-gray-600 line-clamp-2">{post.caption}</p>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab — real reviews from API */}
          <TabsContent value="reviews">
            {reviewsLoading ? (
              <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 bg-gray-200 rounded-xl animate-pulse" />)}</div>
            ) : myReviews.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No reviews yet</h3>
                <p className="text-gray-500 text-sm">Visit a restaurant detail page to leave a review and earn 15 pts!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myReviews.map((review) => (
                  <Card key={review.review_id}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-11 h-11">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white">{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-1">
                            <div>
                              <h4 className="font-semibold text-gray-900">{review.restaurant_name}</h4>
                              {review.restaurant_location && <p className="text-xs text-gray-400">{review.restaurant_location}</p>}
                            </div>
                            <div className="flex items-center gap-0.5">
                              {[1,2,3,4,5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#F4C430] text-[#F4C430]' : 'text-gray-200'}`} />)}
                            </div>
                          </div>
                          {review.dish_name && <p className="text-xs text-[#FF6B35] mb-1">Dish: {review.dish_name}</p>}
                          <p className="text-gray-700 text-sm">{review.comment}</p>
                          <p className="text-xs text-gray-400 mt-2">{formatTimestamp(review.created_at)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Foodlist Tab */}
          <TabsContent value="foodist">
            {/* Collection folders */}
            <div className="mb-5">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                <button
                  onClick={() => setActiveCollection(null)}
                  data-testid="collection-all"
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-all ${
                    activeCollection === null
                      ? 'bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-[#FF6B35]'
                  }`}
                >
                  <List className="w-3.5 h-3.5" />All ({foodlist.length})
                </button>
                {collections.map(c => (
                  <div key={c.collection_id} className="relative group">
                    <button
                      onClick={() => setActiveCollection(activeCollection === c.collection_id ? null : c.collection_id)}
                      data-testid={`collection-${c.collection_id}`}
                      className={`flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-all ${
                        activeCollection === c.collection_id
                          ? 'bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white shadow-sm'
                          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-[#FF6B35]'
                      }`}
                    >
                      <Folder className="w-3.5 h-3.5" />{c.name} ({c.item_count})
                    </button>
                    <button
                      onClick={() => deleteCollection(c.collection_id)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {showNewColl ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      value={newCollName}
                      onChange={e => setNewCollName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && createCollection()}
                      placeholder="Folder name..."
                      autoFocus
                      className="w-32 text-sm px-3 py-2 rounded-2xl border border-[#FF6B35] bg-white dark:bg-gray-800 dark:text-white focus:outline-none"
                    />
                    <button onClick={createCollection} className="p-1.5 rounded-full bg-[#FF6B35] text-white"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => { setShowNewColl(false); setNewCollName(''); }} className="p-1.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500"><XIcon className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewColl(true)}
                    data-testid="create-collection-btn"
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-all whitespace-nowrap"
                  >
                    <FolderPlus className="w-3.5 h-3.5" />New Folder
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const filtered = activeCollection
                ? foodlist.filter(i => i.collection_id === activeCollection)
                : foodlist;
              return filtered.length === 0 ? (
              <div className="text-center py-16">
                <List className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Your Foodlist is empty</h3>
                <p className="text-gray-500 mb-6">Save dishes from AI recommendations to build your food collection</p>
                <Link to="/recommendations">
                  <Button className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">
                    <Sparkles className="w-4 h-4 mr-2" />Explore Recommendations
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filtered.map((item, idx) => (
                  <Card key={item.item_id || idx} className="overflow-hidden hover:border-[#FF6B35] transition-all group">
                    <div className="relative h-48">
                      <img loading="lazy" src={item.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&q=75'} alt={item.name} className="w-full h-full object-cover" />
                      <button
                        data-testid={`remove-foodlist-${idx}`}
                        onClick={() => removeFoodlistItem(item.name, item.restaurant)}
                        className="absolute top-3 right-3 bg-white/90 hover:bg-red-50 text-gray-500 hover:text-red-500 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {/* Collection assign dropdown */}
                      {collections.length > 0 && (
                        <select
                          value={item.collection_id || ''}
                          onChange={e => assignToCollection(item.item_id, e.target.value || null)}
                          className="absolute top-3 left-3 bg-white/90 dark:bg-gray-800/90 text-xs rounded-lg px-2 py-1.5 border-0 opacity-0 group-hover:opacity-100 transition-all focus:outline-none cursor-pointer"
                        >
                          <option value="">No folder</option>
                          {collections.map(c => (
                            <option key={c.collection_id} value={c.collection_id}>{c.name}</option>
                          ))}
                        </select>
                      )}
                      {item.matchScore > 0 && <Badge className="absolute top-3 left-3 bg-green-500 text-white">{item.matchScore}% Match</Badge>}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <MapPin className="w-4 h-4 mr-1 text-[#FF6B35]" />
                        <span>{item.restaurant}</span>
                        {item.location && <span className="ml-2 text-gray-400">· {item.location}</span>}
                      </div>
                      {item.flavorCaption && <p className="text-xs text-gray-500 italic mb-2 line-clamp-2">"{item.flavorCaption}"</p>}
                      <div className="flex items-center justify-between">
                        {item.cuisine && <Badge variant="outline" className={`text-xs border ${getCuisineColor(item.cuisine)}`}>{item.cuisine}</Badge>}
                        {item.price > 0 && <span className="font-semibold text-[#FF6B35]">${item.price}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
            })()}
          </TabsContent>

          {/* Recipes Tab */}
          {/* Collection Sets Tab */}
          <TabsContent value="sets" data-testid="tab-content-sets">
            {setsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => <div key={i} className="h-40 bg-gray-100 rounded-2xl animate-pulse" />)}
              </div>
            ) : collectionSets.length === 0 ? (
              <div className="text-center py-16">
                <Layers className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No collection sets yet</h3>
                <p className="text-gray-500">Start collecting themed food cards!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {collectionSets.map(set => {
                  const progress = (set.collected_count || 0) / (set.cards_needed || 1);
                  const isOpen = expandedSet === set.set_id;
                  return (
                    <div key={set.set_id} className={`bg-white rounded-2xl border overflow-hidden transition-all hover:shadow-md cursor-pointer ${set.completed ? 'border-emerald-200' : 'border-gray-100'}`}
                      onClick={() => setExpandedSet(isOpen ? null : set.set_id)} data-testid={`profile-set-${set.set_id}`}>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{set.emoji || '🍽️'}</span>
                            <div>
                              <h4 className="font-bold text-gray-900 text-sm">{set.title}</h4>
                              <span className="text-[10px] text-gray-400">{set.cuisine} · {set.location}</span>
                            </div>
                          </div>
                          {set.completed && <span className="flex items-center gap-1 bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2 py-0.5 rounded-full"><Trophy className="w-3 h-3" />Done</span>}
                        </div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-bold text-gray-500">{set.collected_count || 0}/{set.cards_needed}</span>
                          <span className="text-[10px] text-[#FF6B35] font-medium">+{set.reward_points} pts</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${set.completed ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#F4C430] to-[#FF6B35]'}`} style={{ width: `${Math.min(progress * 100, 100)}%` }} />
                        </div>
                      </div>
                      {isOpen && (
                        <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-1.5" onClick={e => e.stopPropagation()}>
                          {(set.card_names || []).map((card, i) => {
                            const isCollected = (set.collected || []).includes(card);
                            return (
                              <div key={i} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-gray-50">
                                <div className="flex items-center gap-2">
                                  {isCollected ? <Check className="w-4 h-4 text-emerald-500" /> : <span className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                                  <span className={`text-sm ${isCollected ? 'text-gray-400 line-through' : 'text-gray-700 font-medium'}`}>{card}</span>
                                </div>
                                {!isCollected && (
                                  <button onClick={() => handleCollectCard(set.set_id, card)} disabled={collectingCard === `${set.set_id}-${card}`}
                                    className="px-2.5 py-1 bg-gray-900 text-white text-[10px] font-bold rounded-md hover:bg-gray-800 active:scale-95 disabled:opacity-50" data-testid={`profile-collect-${set.set_id}-${i}`}>
                                    {collectingCard === `${set.set_id}-${card}` ? '...' : 'Collect'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* FoodCard Tab — link to create/browse FoodCards */}
          <TabsContent value="reposts" data-testid="tab-content-reposts">
            {(myReposts.total_first || 0) + (myReposts.total_reposts || 0) === 0 ? (
              <div className="text-center py-16">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Reposts Yet</h3>
                <p className="text-gray-500 mb-5">Repost FoodCards you love to share them with your followers</p>
                <Link to="/surprise-me">
                  <Button className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white gap-2 rounded-2xl px-6">
                    Discover FoodCards
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-4">
                  <Card className="flex-1">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-[#FF6B35]">{myReposts.total_first || 0}</p>
                      <p className="text-xs text-gray-500 font-medium">First Claims</p>
                    </CardContent>
                  </Card>
                  <Card className="flex-1">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-[#FF6B35]">{myReposts.total_reposts || 0}</p>
                      <p className="text-xs text-gray-500 font-medium">Reposts</p>
                    </CardContent>
                  </Card>
                </div>
                {(myReposts.first_claims || []).length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 mb-2">Your First Claims</h4>
                    <div className="space-y-2">
                      {myReposts.first_claims.slice(0, 10).map((c, i) => (
                        <Card key={i}>
                          <CardContent className="p-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{c.dish_name}</p>
                              <p className="text-xs text-gray-500">{c.venue_name}</p>
                            </div>
                            <Badge variant="outline" className="text-[#FF6B35] border-[#FF6B35]/30">+{c.points || 0} pts</Badge>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Taste DNA Tab — AI-generated taste identity */}
          <TabsContent value="taste-dna" data-testid="tab-content-taste-dna">
            <TasteDNAInline user={user} />
          </TabsContent>

          {/* Taste Profile Tab */}
          <TabsContent value="taste">
            <div className="space-y-6">

              {/* Quest Streaks & Badges */}
              <StreakDisplay />

              {/* AI Taste Profile Badge */}
              {profileLoading ? (
                <Card className="animate-pulse">
                  <CardContent className="p-8 flex items-center gap-6">
                    <div className="w-20 h-20 bg-gray-200 rounded-full" />
                    <div className="flex-1 space-y-3">
                      <div className="h-6 bg-gray-200 rounded w-1/2" />
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-1/3" />
                    </div>
                  </CardContent>
                </Card>
              ) : tasteProfile ? (
                <Card className="overflow-hidden border-2 border-[#FF6B35]/20">
                  <div className="bg-gradient-to-r from-[#F4C430]/10 via-[#FF8C42]/10 to-[#FF6B35]/10 p-6">
                    <div className="flex items-start gap-5">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#F4C430] via-[#FF8C42] to-[#FF6B35] rounded-2xl flex items-center justify-center text-4xl shadow-lg flex-shrink-0">
                        {tasteProfile.emoji || '🍽️'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-2xl font-bold text-gray-900">{tasteProfile.badge}</h3>
                          <Badge className="bg-[#FF6B35] text-white text-xs">{tasteProfile.personality}</Badge>
                        </div>
                        <p className="text-gray-600 mb-4">{tasteProfile.description}</p>
                        <div className="flex items-center gap-4 text-sm flex-wrap">
                          <span className="text-gray-500 flex items-center gap-1"><Search className="w-4 h-4" />{tasteProfile.total_searches} searches</span>
                          <span className="text-gray-500 flex items-center gap-1"><List className="w-4 h-4" />{tasteProfile.total_saves} saved</span>
                          <span className="text-gray-500 flex items-center gap-1"><TrendingUp className="w-4 h-4" />{tasteProfile.activity_count} interactions</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button data-testid="share-taste-profile-btn" onClick={() => setShowShareCard(true)}
                        className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white gap-2" size="sm">
                        <Share2 className="w-4 h-4" />Share My Badge
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Award className="w-16 h-16 text-[#FF6B35] mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Discover Your Food Personality</h3>
                    <p className="text-gray-600 mb-6">Based on your searches, saves, and preferences, we'll generate your unique food personality badge.</p>
                    <Button
                      data-testid="generate-taste-profile-btn"
                      onClick={fetchTasteProfile}
                      className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />Generate My Food Personality
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Top Cuisines */}
              {(tasteProfile?.top_cuisines?.length > 0 || topCuisines.length > 0) && (
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#FF6B35]" />Your Top Cuisines
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {(tasteProfile?.top_cuisines || topCuisines).map((c, i) => (
                        <div key={c} className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs font-bold w-5 text-right">#{i + 1}</span>
                          <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getCuisineColor(c)}`}>{c}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Searches */}
              {(tasteProfile?.recent_searches?.length > 0 || activitySummary?.recent_searches?.length > 0) && (
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#FF6B35]" />Recent Searches
                    </h4>
                    <div className="space-y-3">
                      {(tasteProfile?.recent_searches || activitySummary?.recent_searches || []).map((s, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{s.comfort === 'dine-out' ? '🍽️' : s.comfort === 'dine-in' ? '🏠' : s.comfort === 'cook-at-home' ? '👨‍🍳' : '📅'}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {COMFORT_LABELS[s.comfort] || s.comfort}
                                {s.cuisine && <span className="text-[#FF6B35] ml-1">· {s.cuisine}</span>}
                                {s.vibe && <span className="text-gray-500 ml-1">· {s.vibe}</span>}
                              </p>
                              {s.location && <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</p>}
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">{formatTimestamp(s.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Discover Food Buddies */}
              <DiscoverFriends />

              {/* Instagram Connect */}
              <InstagramConnect />

              {/* Explore CTA if no data yet */}
              {!tasteProfile && !activitySummary?.recent_searches?.length && (
                <Card>
                  <CardContent className="p-6 text-center">
                    <Sparkles className="w-12 h-12 text-[#FF6B35] mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">Explore recommendations and save dishes to build your taste profile!</p>
                    <div className="flex justify-center gap-3">
                      {[
                        { label: 'Dine Out', path: '/recommendations?comfort=dine-out' },
                        { label: 'Surprise Me', path: '/recommendations?comfort=surprise-me' },
                      ].map(({ label, path }) => (
                        <Link key={label} to={path}>
                          <Button variant="outline" className="border-[#FF6B35] text-[#FF6B35]">{label}</Button>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          <TabsContent value="addresses">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Saved Addresses</h3>
                <Button
                  data-testid="add-address-btn"
                  onClick={() => setShowAddressForm(true)}
                  className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white gap-2"
                >
                  <Plus className="w-4 h-4" />Add Address
                </Button>
              </div>

              {showAddressForm && (
                <Card data-testid="address-form-card" className="border border-[#FF6B35]/20">
                  <CardContent className="p-6 space-y-5">
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-900">Label</p>
                      <div className="flex flex-wrap gap-2">
                        {['home', 'office', 'custom'].map((label) => (
                          <button
                            key={label}
                            data-testid={`address-label-${label}`}
                            onClick={() => setAddressLabelType(label)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${addressLabelType === label ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'bg-white border-gray-200 text-gray-600 hover:border-[#FF6B35]'}`}
                          >
                            {label.charAt(0).toUpperCase() + label.slice(1)}
                          </button>
                        ))}
                      </div>
                      {addressLabelType === 'custom' && (
                        <input
                          data-testid="address-custom-label-input"
                          value={customAddressLabel}
                          onChange={(e) => setCustomAddressLabel(e.target.value)}
                          placeholder="Custom label"
                          className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-900">Search Address</p>
                      <LocationSearchInput
                        testIdPrefix="saved-address"
                        onSelect={setAddressLocation}
                        placeholder="Search for a location..."
                        className="w-full"
                      />
                    </div>

                    {addressMapUrl ? (
                      <div className="rounded-2xl overflow-hidden border border-gray-100">
                        <iframe
                          data-testid="saved-address-map"
                          title="saved-address-map"
                          src={addressMapUrl}
                          className="w-full border-0"
                          style={{ height: '220px' }}
                          sandbox="allow-scripts allow-same-origin"
                        />
                      </div>
                    ) : (
                      <div data-testid="saved-address-map-empty" className="h-40 rounded-2xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                        Map preview will appear here
                      </div>
                    )}

                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-900">Notes (optional)</p>
                      <textarea
                        data-testid="address-notes-input"
                        value={addressNotes}
                        onChange={(e) => setAddressNotes(e.target.value)}
                        rows={3}
                        placeholder="e.g., gate code, parking tips"
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        data-testid="address-cancel-btn"
                        variant="outline"
                        onClick={() => { setShowAddressForm(false); resetAddressForm(); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="address-save-btn"
                        onClick={createAddress}
                        disabled={!addressLocation || addressSaving}
                        className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white"
                      >
                        {addressSaving ? 'Saving...' : 'Save Address'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {addressesLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : savedAddresses.length === 0 ? (
                <Card data-testid="addresses-empty-state">
                  <CardContent className="p-8 text-center">
                    <MapPin className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No saved addresses yet</h3>
                    <p className="text-gray-500 text-sm">Add Home, Office, or a favorite spot for faster recommendations.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {savedAddresses.map((address) => (
                    <Card key={address.address_id} data-testid={`saved-address-card-${address.address_id}`}>
                      <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span data-testid={`saved-address-label-${address.address_id}`} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FFF5F0] text-[#FF6B35]">
                              {address.label}
                            </span>
                            <span data-testid={`saved-address-display-${address.address_id}`} className="text-sm font-semibold text-gray-900 truncate">
                              {address.display_name}
                            </span>
                          </div>
                          {address.full_address && (
                            <p className="text-xs text-gray-500 mt-1 truncate">{address.full_address}</p>
                          )}
                          {address.notes && (
                            <p className="text-xs text-gray-400 mt-1">{address.notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            data-testid={`saved-address-use-${address.address_id}`}
                            variant="outline"
                            onClick={() => handleUseAddress(address)}
                            className="border-[#FF6B35] text-[#FF6B35]"
                          >
                            Use
                          </Button>
                          <button
                            data-testid={`saved-address-delete-${address.address_id}`}
                            onClick={() => deleteAddress(address.address_id)}
                            className="w-9 h-9 rounded-full border border-red-100 text-red-500 hover:bg-red-50 flex items-center justify-center"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Taste Profile Share Card Modal */}
      {showShareCard && (
        <TasteProfileShareCard
          profile={tasteProfile}
          user={user}
          onClose={() => setShowShareCard(false)}
        />
      )}

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      <FollowListModal
        isOpen={followModal.open}
        onClose={() => setFollowModal({ open: false, tab: 'followers' })}
        tab={followModal.tab}
        userId={user?.user_id}
      />
    </div>
  );
};

export default Profile;
